if ( typeof InteractiveMap !== 'function' ) {

  class InteractiveMap extends HTMLElement {

    constructor(){
      super();
      this.markers = {};
      this._pendingMarker = null;
      this._fitted = false;

      const initObserver = new IntersectionObserver((entries, observer) => {
        if ( ! entries[0].isIntersecting ) return;
        this.init();
        observer.unobserve(this);
      });
      initObserver.observe(this);

      setTimeout(() => {
        this.init();
        initObserver.unobserve(this);
      }, 10000);
    }

    init(){
      if ( this._started ) return;
      this._started = true;
      this.loadLeaflet()
        .then(() => this.createMap())
        .catch(() => this.fallback());
    }

    loadLeaflet(){
      if ( window.L ) return Promise.resolve();
      if ( ! window.loadingLeaflet ) {
        window.loadingLeaflet = new Promise((resolve, reject) => {
          const css = document.createElement('link');
          css.rel = 'stylesheet';
          css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
          css.crossOrigin = '';
          document.head.appendChild(css);

          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
          script.crossOrigin = '';
          script.onload = () => resolve();
          script.onerror = () => reject();
          document.head.appendChild(script);
        });
      }
      return window.loadingLeaflet;
    }

    createMap(){
      const mapEl = this.querySelector('[data-js-map-object]');
      const mapMarkers = this.querySelectorAll('[data-js-map-addresses] li');
      if ( ! mapEl || mapMarkers.length === 0 ) {
        this._hideLoader();
        return;
      }

      const map = L.map(mapEl, {
        scrollWheelZoom: true,
        minZoom: 2
      });
      this.map = map;
      this._hideLoader();

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(map);

      const bounds = [];
      mapMarkers.forEach(elm => {
        const lat = parseFloat(elm.dataset.latitude);
        const lng = parseFloat(elm.dataset.longitude);
        if ( Number.isNaN(lat) || Number.isNaN(lng) ) return;

        const marker = L.marker([lat, lng], {
          icon: this._icon(elm.dataset.marker, false)
        }).addTo(map);

        marker.on('click', () => this._activate(elm.dataset.id, true));
        this.markers[elm.dataset.id] = marker;
        bounds.push([lat, lng]);
      });

      this._bounds = bounds;
      this._fit();

      if ( typeof ResizeObserver !== 'undefined' ) {
        this._resizeObserver = new ResizeObserver(() => {
          if ( this.offsetWidth === 0 ) return;
          map.invalidateSize();
          if ( ! this._fitted ) this._fit();
        });
        this._resizeObserver.observe(this);
      }

      if ( this._pendingMarker ) this.selectMarker(this._pendingMarker);
      this.dispatchEvent(new CustomEvent('ready'));
    }

    selectMarker(id){
      this._pendingMarker = id;
      if ( this.markers && this.markers[id] ) this._activate(id, true);
    }

    _zoom(){
      const configured = parseInt(this.dataset.zoom, 10);
      if ( ! configured || configured < 12 ) return 13;
      return Math.min(configured, 16);
    }

    _focusLatLng(){
      if ( this._pendingMarker && this.markers[this._pendingMarker] ) {
        return this.markers[this._pendingMarker].getLatLng();
      }
      return this._bounds[0];
    }

    _fit(){
      if ( ! this.map || ! this._bounds || this._bounds.length === 0 || this.offsetWidth === 0 ) return;
      this.map.setView(this._focusLatLng(), this._zoom());
      this._fitted = true;
    }

    _icon(url, selected){
      const size = selected ? 42 : 30;
      return L.icon({
        iconUrl: url,
        iconSize: [size, size],
        iconAnchor: [size / 2, size],
        className: selected ? 'store-map-pin store-map-pin--selected' : 'store-map-pin'
      });
    }

    _activate(id, notify){
      const marker = this.markers[id];
      if ( ! marker ) return;
      Object.keys(this.markers).forEach(key => {
        const elm = this.querySelector(`[data-js-map-addresses] li[data-id="${key}"]`);
        this.markers[key].setIcon(this._icon(elm ? elm.dataset.marker : '', key === id));
      });
      const zoom = Math.max(this.map.getZoom(), this._zoom());
      this.map.setView(marker.getLatLng(), zoom);
      if ( notify ) {
        this.dispatchEvent(new CustomEvent('marker', { detail: { store: id } }));
      }
    }

    _hideLoader(){
      const loader = this.querySelector('.map-loader');
      if ( loader ) loader.hidden = true;
    }

    fallback(){
      this._hideLoader();
      const fallback = this.querySelector('[data-js-map-fallback]');
      const object = this.querySelector('[data-js-map-object]');
      if ( fallback ) fallback.style.display = 'block';
      if ( object ) object.style.display = 'none';
    }

  }

  if ( typeof customElements.get('interactive-map') == 'undefined' ) {
    customElements.define('interactive-map', InteractiveMap);
  }

}
